import {
  Card,
  DiffViewerContentCols,
  DiffViewerTitleCols,
  Row,
} from '@/components';
import { useMinistries } from '@/features/master-table/hooks';
import { diffRenderValueFormatter } from '@/features/project/_utils/diff-render-value-formatter';
import { getAdditionalJurisdictionalUserNames } from '@/features/project/_utils/format-project-data';
import { isBefore2025 } from '@/features/project/_utils/project';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import { ITEM_SPLITTER, NEST_ITEM_CONNECTOR } from '@/utils';
import {
  FundSheetTypeLabelModifier,
  SheetTypeLabelModifier,
} from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  BusinessFormLabelModifier,
  CorporateFormLabelModifier,
  type FundAdditionalAttributesModel,
  FundNecessityTypeLabelModifier,
  type FundSheetModel,
  OperationFormLabelModifier,
  type ProjectBaseModel,
} from '../../../_models/project';

type Project = z.infer<typeof FundSheetModel>;

type Props = {
  sourceProject: Project; // left side
  targetProject: Project; // right side
};

type DiffViewConfigItem = DiffViewConfigType<Project> & {
  hidden?: boolean;
};

export const KsProjectDiffView: FC<Props> = ({
  sourceProject,
  targetProject,
}) => {
  const { t } = useTranslation();
  // NOTE: PRZ-925 2025年のKSレイアウト変更に伴い、表示する項目が大きく変わった。
  // 2024年度と2025年度の差分表示では、レイアウト変更に伴い追加された項目・削除された項目を共に表示する。
  // 2025年度以降の差分表示では、レイアウト変更に伴い削除された項目は表示しない。
  const isBefore2025Source = isBefore2025(sourceProject);
  const isBefore2025Target = isBefore2025(targetProject);
  const organizations = useOrganizations();
  const users = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: [
        ...new Set([
          ...(sourceProject.peopleInChargeIds ?? []),
          ...sourceProject.businessManagerIds,
          ...(sourceProject.fundAdditionalAttributes?.additionalJurisdictionalUsers?.flatMap(
            (v) => [...v.businessManagerIds, ...v.peopleInChargeIds],
          ) ?? []),
          ...(targetProject.peopleInChargeIds ?? []),
          ...targetProject.businessManagerIds,
          ...(targetProject.fundAdditionalAttributes?.additionalJurisdictionalUsers?.flatMap(
            (v) => [...v.businessManagerIds, ...v.peopleInChargeIds],
          ) ?? []),
        ]),
      ],
      showDeleted: true,
    },
  });
  const ministries = useMinistries();
  const fundSheetType = sourceProject.fundAdditionalAttributes?.fundSheetType;

  const diffViewConfig: Array<DiffViewConfigItem> = [
    {
      prop: 'sheetType',
      label: t('sheet-type'),
      converter: (_, project) => {
        return [
          SheetTypeLabelModifier.KS,
          ...(project?.fundAdditionalAttributes
            ? [
                FundSheetTypeLabelModifier[
                  project.fundAdditionalAttributes.fundSheetType
                ],
              ]
            : []),
        ].join(NEST_ITEM_CONNECTOR);
      },
    },
    {
      prop: 'fundAdditionalAttributes.fundSheetNumber',
      label: t('fund-sheet-number'),
    },
    {
      prop: 'fundAdditionalAttributes.fundSheetBranchNumber',
      label: t('fund-sheet-branch-number'),
    },
    {
      prop: 'ministryId',
      label: t('ministry'),
      converter: (value: string) =>
        diffRenderValueFormatter.ministryId(value, ministries.data),
    },
    {
      prop: 'organizationId',
      label: t('organization-in-charge'),
      converter: (value: string) =>
        diffRenderValueFormatter.organizationId(value, organizations.data),
    },
    {
      prop: 'peopleInChargeIds',
      label: t('people-in-charge'),
      converter: (value) =>
        diffRenderValueFormatter.peopleInChargeIds(
          value ?? [],
          users.data?.results,
        ),
    },
    {
      prop: 'businessManagerIds',
      label: t('business-manager'),
      converter: (value: string[]) =>
        diffRenderValueFormatter.businessManagerIds(value, users.data?.results),
    },
    {
      prop: 'otherResponsibleOrganizationIds',
      label: t('other-organization-in-charge'),
      converter: (values: string[]) =>
        diffRenderValueFormatter.otherResponsibleOrganizationIds(
          values,
          organizations.data,
        ),
    },
    {
      prop: 'relatedOrganizationIds',
      label: t('related-organizations'),
      converter: (
        values: z.infer<typeof ProjectBaseModel.shape.relatedOrganizationIds>,
      ) =>
        diffRenderValueFormatter.relatedOrganizationIds(
          values,
          organizations.data,
        ),
    },
    {
      prop: 'fundAdditionalAttributes.additionalJurisdictionalUsers',
      label: t('additional-organization-in-charge'),
      hidden: fundSheetType !== 'standard',
      converter: (value) => {
        const data = getAdditionalJurisdictionalUserNames(
          users.data?.results ?? [],
          organizations.data ?? [],
          value,
        );

        return data
          .map(
            (v) =>
              `${v.organizationName}\n${v.businessManagerNames.join(
                ', ',
              )}\n${v.peopleInChargeNames.join(', ')}`,
          )
          .join('\n\n');
      },
    },
    { prop: 'projectNumber', label: t('project-number') },
    { prop: 'name', label: t('fund-name') },
    {
      prop: 'fundAdditionalAttributes.fundBusinessName',
      label: t('fund-business-name'),
      hidden:
        fundSheetType !== 'standard' && fundSheetType !== 'local-government',
    },
    {
      prop: 'fundAdditionalAttributes.fundOwner',
      label: t('fund-owner'),
      hidden: fundSheetType !== 'standard',
    },
    {
      prop: 'fundAdditionalAttributes.fundOwner',
      label: t('corporate-name'),
      hidden: fundSheetType !== 'investment',
    },
    { prop: 'fiscalYear', label: t('fiscal-year') },
    {
      prop: 'fundAdditionalAttributes.corporateForm',
      label: t('corporate-form'),
      hidden: fundSheetType !== 'standard',
      converter: (
        value: z.infer<typeof FundAdditionalAttributesModel>['corporateForm'],
      ) => (value ? CorporateFormLabelModifier[value] : ''),
    },
    {
      prop: 'fundAdditionalAttributes.optionalCorporateForm',
      label: t('optional-corporate-form'),
      hidden: fundSheetType !== 'standard',
    },
    {
      prop: 'inspectionEvaluation.eligibilityInspection.methodAndReasonsForSelection',
      label: t('eligibility-inspection-method-and-reasons-for-selection'),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'inspectionEvaluation.eligibilityInspection.results',
      label: t('eligibility-inspection-results'),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'inspectionEvaluation.implementationStatusOfInspection',
      label: t('implementation-status-of-inspection'),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'startYear',
      label: t('start-fiscal-year-of-project'),
      hidden: fundSheetType !== 'standard' && fundSheetType !== 'investment',
      converter: (_, project) => diffRenderValueFormatter.startYear(project),
    },
    {
      prop: 'endYear',
      label: t('end-fiscal-year-of-project'),
      hidden: fundSheetType !== 'standard' && fundSheetType !== 'investment',
      converter: (_, project) => diffRenderValueFormatter.endYear(project),
    },
    {
      prop: 'fundAdditionalAttributes.operationForms',
      label: t('operation-form'),
      hidden:
        fundSheetType !== 'standard' && fundSheetType !== 'local-government',
      converter: (
        value: z.infer<typeof FundAdditionalAttributesModel>['operationForms'],
      ) =>
        value?.map((v) => OperationFormLabelModifier[v]).join(ITEM_SPLITTER) ??
        '',
    },
    {
      prop: 'fundAdditionalAttributes.businessForms',
      label: t('business-form'),
      hidden:
        fundSheetType !== 'standard' && fundSheetType !== 'local-government',
      converter: (
        value: z.infer<typeof FundAdditionalAttributesModel>['businessForms'],
      ) =>
        value?.map((v) => BusinessFormLabelModifier[v]).join(ITEM_SPLITTER) ??
        '',
    },
    {
      prop: 'relatedProjects',
      label: t('related-projects'),
      hidden: fundSheetType !== 'standard',
      converter: (
        value: z.infer<typeof ProjectBaseModel.shape.relatedProjects>,
      ) => diffRenderValueFormatter.relatedProjects(value),
    },
    {
      prop: 'purpose',
      label: t('project-purpose'),
      isLongField: true,
      hidden: fundSheetType !== 'standard',
    },
    {
      prop: 'task',
      label: t('project-task'),
      isLongField: true,
      hidden: fundSheetType !== 'standard',
    },
    {
      prop: 'overview',
      label: t('project-overview'),
      isLongField: true,
    },
    {
      prop: 'overviewUrl',
      label: t('overview-url'),
      hidden: fundSheetType !== 'standard',
    },
    {
      prop: 'fundAdditionalAttributes.necessityType',
      label: [t('necessity-of-fund-method'), t('fund-necessity-type')].join(
        ': ',
      ),
      hidden:
        (fundSheetType !== 'standard' &&
          fundSheetType !== 'local-government') ||
        isBefore2025Source,
      converter: (
        value:
          | z.infer<typeof FundAdditionalAttributesModel.shape.necessityType>
          | undefined,
      ) => (value ? FundNecessityTypeLabelModifier[value] : ''),
    },
    {
      prop: 'fundAdditionalAttributes.necessityReasons',
      label: [
        t('necessity-of-fund-method'),
        t('fund-necessity-reasons-for-local-government'),
      ].join(': '),
      isLongField: true,
      hidden:
        (fundSheetType !== 'standard' &&
          fundSheetType !== 'local-government') ||
        isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.endDate',
      label:
        fundSheetType === 'local-government'
          ? t('fund-scheduled-end-period-for-local-government')
          : t('fund-scheduled-end-period'),
      hidden:
        (fundSheetType !== 'standard' &&
          fundSheetType !== 'local-government') ||
        isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.supplementaryReasonsForEndPeriod',
      label: [t('fund-scheduled-end-period'), t('supplementary-reasons')].join(
        ': ',
      ),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.reasonsForChangingScheduledEndPeriod',
      label: [
        t('fund-scheduled-end-period'),
        t('fund-necessity-reasons-for-local-government'),
      ].join(': '),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.endDateOfNewApplicationReceiving',
      label:
        fundSheetType === 'local-government'
          ? t('end-date-of-new-application-for-local-government')
          : t('end-date-of-new-application'),
      hidden:
        (fundSheetType !== 'standard' &&
          fundSheetType !== 'local-government') ||
        isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.supplementaryReasonsForNewApplicationReceiving',
      label: [
        t('end-date-of-new-application'),
        t('supplementary-reasons'),
      ].join(': '),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.scheduledEnd.reasonsForChangingEndPeriodOfNewApplicationReceiving',
      label: [
        t('end-date-of-new-application'),
        t('fund-necessity-reasons-for-local-government'),
      ].join(': '),
      isLongField: true,
      hidden: fundSheetType !== 'standard' || isBefore2025Source,
    },
    {
      prop: 'fundAdditionalAttributes.effortAndResult',
      label: t('effort-and-result'),
      hidden: fundSheetType !== 'standard' || !isBefore2025Target,
    },
    {
      prop: 'legalBases',
      label: t('legal-bases'),
      isLongField: true,
      hidden: fundSheetType !== 'standard',
      converter: (
        value: z.infer<typeof FundSheetModel.shape.legalBases> | undefined,
      ) => diffRenderValueFormatter.legalBases(value),
    },
    {
      prop: 'plans',
      label: t('plan'),
      isLongField: true,
      hidden: fundSheetType !== 'standard',
      converter: (value: z.infer<typeof FundSheetModel.shape.plans>) =>
        diffRenderValueFormatter.plans(value),
    },
    {
      prop: 'task',
      label: t('status-of-response-by-ministries'),
      hidden: fundSheetType !== 'local-government',
    },
    { prop: 'remark', label: t('remark'), isLongField: true },
  ];

  return (
    <Card>
      <Row gutter={[8, 0]}>
        <DiffViewerTitleCols label={t('project')} />
        {diffViewConfig.flatMap(
          ({ prop, label, converter, isLongField, hidden }) =>
            hidden ? (
              []
            ) : (
              <DiffViewerContentCols<Project>
                key={`project-${prop}`}
                legend={label}
                prop={prop}
                textConverter={converter}
                isLongField={isLongField}
                source={sourceProject}
                target={targetProject}
              />
            ),
        )}
      </Row>
    </Card>
  );
};
